import { useEffect, useState } from 'react';
import { countriesWithPhone, documentTypeOptions } from './constants';
import { getAssistCountries } from '@shared/services/services';
import GenericDropdown from '@shared/components/GenericDropdown';
import StyledTextField from '@shared/components/StyledTextField';
import FormSection from '@shared/components/FormSection';
import { calculateAge } from '@shared/helpers/calculateAge';

const FormPersonalData = ({ index, formik, elementSelected }) => {
  const [countries, setCountries] = useState([]);

  // Fetch países
  const fetchAssistCountries = async () => {
    const result = await getAssistCountries();
    setCountries(result);
  };

  useEffect(() => {
    fetchAssistCountries();
  }, []);

  // Items tipo documento
  const typeDNIItems = documentTypeOptions.map((item, i) => ({
    id: `doc-type-${item.formikValue}-${i}`,
    value: item.formikValue,
    fragments: [{ text: item.label, bold: false }],
  }));

  // Helper para scroll al primer error
  useEffect(() => {
    const fieldNames = [
      'name',
      'last_name',
      'birthdate',
      'document_type',
      'document_number',
      'country',
      ...(index === 0 ? ['codePhone', 'caracteristicPhone', 'phone'] : []),
    ];
    for (const name of fieldNames) {
      const meta = formik.getFieldMeta(`passengers[${index}].${name}`);
      if (meta.touched && meta.error) {
        const input = document.getElementById(`passenger${index}-${name}`);
        if (input) {
          setTimeout(() => {
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 50);
        }
        break;
      }
    }
  }, [formik, index]);

  const getPassengerAgeLabel = index => {
    const expectedBirthdate =
      elementSelected?.clientesCotizados?.clienteCotizacion?.[index]
        ?.fechaNacimiento;

    if (!expectedBirthdate) return `Pasajero (${index + 1})`;

    const age = calculateAge(expectedBirthdate);

    if (age < 70) return 'Pasajero (Hasta 69 años)';
    if (age < 75) return 'Pasajero (70 a 74 años)';
    return 'Pasajero (75 años o más)';
  };

  return (
    <FormSection
      title={getPassengerAgeLabel(index)}
      customLayout={{
        md: {
          1: 3,
          2: 3,
          3: 1.9,
          4: 1.8,
          5: 2.3,
          6: 3,
          7: 3,
          8: 1.9,
          9: 4.1,
        },
      }}
    >
      {/* Nombre */}
      <StyledTextField
        name={`passengers[${index}].name`}
        label="Nombre (según documento)"
        formik={formik}
      />

      {/* Apellido */}
      <StyledTextField
        name={`passengers[${index}].last_name`}
        label="Apellido (según documento)"
        formik={formik}
      />

      {/* Fecha de Nacimiento */}
      <StyledTextField
        name={`passengers[${index}].birthdate`}
        label={'Fecha de Nacimiento'}
        type="date"
        formik={formik}
      />

      {/* Tipo de Documento */}
      <GenericDropdown
        items={typeDNIItems}
        selectedItem={{
          id: formik.values.passengers[index].document_type,
          value: formik.values.passengers[index].document_type,
          fragments: [
            {
              text: documentTypeOptions.find(
                opt =>
                  opt.formikValue ===
                  formik.values.passengers[index].document_type
              )?.label,
              bold: false,
            },
          ],
        }}
        onSelect={item =>
          formik.setFieldValue(`passengers[${index}].document_type`, item.value)
        }
        buttonLabel="Tipo Doc."
      />

      {/* Número de Documento */}
      <StyledTextField
        name={`passengers[${index}].document_number`}
        label={
          formik.values.passengers[index].document_type === 'DNI'
            ? 'Número de DNI'
            : 'Número de Pasaporte'
        }
        formik={formik}
        inputProps={
          formik.values.passengers[index].document_type === 'DNI'
            ? { maxLength: 8, inputMode: 'numeric', pattern: '[0-9]*' }
            : { maxLength: 20 }
        }
        onChange={e => {
          const { value } = e.target;

          // Si es DNI, aplicamos el filtro de "solo números" inmediatamente
          if (formik.values.passengers[index].document_type === 'DNI') {
            const cleanValue = value.replace(/\D/g, ''); // Elimina cualquier cosa que no sea número

            // Actualizamos Formik con el valor filtrado
            formik.setFieldValue(
              `passengers[${index}].document_number`,
              cleanValue
            );
          } else {
            // Si no es DNI (ej. Pasaporte), dejamos que Formik maneje el cambio normal
            formik.handleChange(e);
          }
        }}
      />

      {/* País */}
      <GenericDropdown
        items={countries.map((c, i) => ({
          id: `country-${c.code}-${i}`,
          value: c.code,
          fragments: [{ text: c.description, bold: false }],
        }))}
        selectedItem={
          formik.values.passengers[index].country
            ? {
                id: formik.values.passengers[index].country,
                value: formik.values.passengers[index].country,
                fragments: [
                  {
                    text: countries.find(
                      c => c.code === formik.values.passengers[index].country
                    )?.description,
                    bold: false,
                  },
                ],
              }
            : null
        }
        onSelect={item =>
          formik.setFieldValue(`passengers[${index}].country`, item.value)
        }
        buttonLabel="País de nacimiento"
        hasError={
          !!formik.getFieldMeta(`passengers[${index}].country`).error &&
          formik.getFieldMeta(`passengers[${index}].country`).touched
        }
        helperText={
          formik.getFieldMeta(`passengers[${index}].country`).error ?? ''
        }
      />

      {/* Campos de teléfono solo para primer pasajero */}
      {index === 0 && (
        <>
          {/* Código de país */}
          <GenericDropdown
            items={countriesWithPhone.map((c, i) => ({
              id: `phone-${c.value}-${i}`,
              value: c.value,
              fragments: [{ text: c.label }],
            }))}
            selectedItem={{
              id: formik.values.passengers[index].codePhone,
              value: formik.values.passengers[index].codePhone,
              fragments: [
                {
                  text: countriesWithPhone.find(
                    c => c.value === formik.values.passengers[index].codePhone
                  )?.label,
                  bold: false,
                },
              ],
            }}
            onSelect={item =>
              formik.setFieldValue(`passengers[${index}].codePhone`, item.value)
            }
            buttonLabel="Código"
          />

          {/* Característica */}
          <StyledTextField
            name={`passengers[${index}].caracteristicPhone`}
            label="Cod. de área"
            formik={formik}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />

          {/* Teléfono */}
          <StyledTextField
            name={`passengers[${index}].phone`}
            label="Celular (sin el 15)"
            formik={formik}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        </>
      )}
    </FormSection>
  );
};

export default FormPersonalData;
